/**
 * RouteEntityContext - Tracks Current Page Entity
 * 
 * Provides the current page's entity information (type and sourceId) to child components.
 * This is essential for language switching, where we need to know the current entity
 * to look up its translated slug in the target locale's manifest.
 * 
 * Usage:
 * - DeterministicRouter sets the current entity when it resolves a slug
 * - Language selector reads the entity to compute the target URL
 * 
 * @example
 * // In DeterministicRouter
 * const { setCurrentEntity } = useRouteEntity();
 * setCurrentEntity({ type: 'location', sourceId: '45' });
 * 
 * // In language selector
 * const { currentEntity } = useRouteEntity();
 * if (currentEntity?.type === 'location') {
 *   const targetSlug = await fetchTranslatedSlug('location', currentEntity.sourceId, targetLocale);
 * }
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// ============================================================================
// TYPES
// ============================================================================

export type EntityType = 'route' | 'page' | 'location' | 'expert' | 'home' | 'dynamic';

export interface RouteEntity {
  /** The type of entity currently being displayed */
  type: EntityType;
  /** The source ID of the entity (null for home/dynamic) */
  sourceId: string | null;
  /** The current slug (for reference) */
  slug?: string;
  /** Whether async resolution is in progress (INV-8) */
  isPending?: boolean;
  /** Optional metadata for dynamic entities (e.g., topic slug for INV-7, publication data for INV-9) */
  metadata?: {
    topicSlug?: string;
    topicId?: string;
    // Publication metadata for language switching (INV-9 revised)
    isPublication?: boolean;
    publicationId?: string;
    authorSlug?: string;
    publicationSlug?: string;
  };
}

export interface PendingLanguageSwitch {
  /** Target locale to switch to */
  targetLocale: string;
  /** Entity type at time of switch */
  entityType: EntityType;
  /** Entity source ID at time of switch */
  entitySourceId: string | null;
  /** Display name for the locale */
  displayName?: string;
  /** Timestamp when this switch was initiated (for TTL enforcement - INV-5) */
  createdAt: number;
  /** Optional metadata for dynamic entities (e.g., topic slug, publication data) */
  metadata?: {
    topicSlug?: string;
    topicId?: string;
    topicsPrefix?: string;
    // Publication metadata for language switching (INV-9 revised)
    isPublication?: boolean;
    publicationId?: string;
    authorSlug?: string;
    publicationSlug?: string;
  };
}

export interface RouteEntityContextValue {
  /** Current page entity */
  currentEntity: RouteEntity | null;
  /** Set the current page entity */
  setCurrentEntity: (entity: RouteEntity | null) => void;
  /** Clear the current entity */
  clearEntity: () => void;
  /** Pending language switch (set before locale change, cleared after navigation) */
  pendingLanguageSwitch: PendingLanguageSwitch | null;
  /** Set pending language switch */
  setPendingLanguageSwitch: (pending: PendingLanguageSwitch | null) => void;
  /** Whether an async entity resolution is in progress (INV-8) */
  isResolvingEntity: boolean;
  /** Set entity resolution state */
  setIsResolvingEntity: (resolving: boolean) => void;
  /** Whether a language switch is in progress (prevents slug re-resolution race) */
  isLanguageSwitchPending: boolean;
}

// ============================================================================
// CONTEXT
// ============================================================================

const RouteEntityContext = createContext<RouteEntityContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface RouteEntityProviderProps {
  children: React.ReactNode;
}

export const RouteEntityProvider: React.FC<RouteEntityProviderProps> = ({ children }) => {
  const [currentEntity, setCurrentEntityState] = useState<RouteEntity | null>(null);
  const [pendingLanguageSwitch, setPendingLanguageSwitchState] = useState<PendingLanguageSwitch | null>(null);
  const [isResolvingEntity, setIsResolvingEntityState] = useState<boolean>(false);

  const setCurrentEntity = useCallback((entity: RouteEntity | null) => {
    setCurrentEntityState(entity);
  }, []);

  const clearEntity = useCallback(() => {
    setCurrentEntityState(null);
  }, []);

  const setPendingLanguageSwitch = useCallback((pending: PendingLanguageSwitch | null) => {
    setPendingLanguageSwitchState(pending);
  }, []);

  const setIsResolvingEntity = useCallback((resolving: boolean) => {
    setIsResolvingEntityState(resolving);
  }, []);

  // Derived state: language switch is in progress (prevents slug re-resolution race)
  const isLanguageSwitchPending = pendingLanguageSwitch !== null;

  const value = useMemo<RouteEntityContextValue>(() => ({
    currentEntity,
    setCurrentEntity,
    clearEntity,
    pendingLanguageSwitch,
    setPendingLanguageSwitch,
    isResolvingEntity,
    setIsResolvingEntity,
    isLanguageSwitchPending,
  }), [currentEntity, setCurrentEntity, clearEntity, pendingLanguageSwitch, setPendingLanguageSwitch, isResolvingEntity, setIsResolvingEntity, isLanguageSwitchPending]);

  return (
    <RouteEntityContext.Provider value={value}>
      {children}
    </RouteEntityContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * Access the current route entity context
 * 
 * @throws Error if used outside RouteEntityProvider
 */
export function useRouteEntity(): RouteEntityContextValue {
  const context = useContext(RouteEntityContext);
  
  if (!context) {
    throw new Error('useRouteEntity must be used within RouteEntityProvider');
  }
  
  return context;
}

/**
 * Safe version that returns null if not in provider (for optional use)
 */
export function useRouteEntitySafe(): RouteEntityContextValue | null {
  return useContext(RouteEntityContext);
}

// ============================================================================
// EXPORTS
// ============================================================================

export { RouteEntityContext };
